import { safeJson } from "src/app/helpers/Index";
import { faArrowsRotate, faBan, faBullseye, faClockRotateLeft, faFileLines, faFutbol, faImage, faPowerOff, faRightLeft, faShirt, faStopwatch, faTextHeight, faTriangleExclamation, faVideo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import React from "react";
import { ClubContext } from "src/app/helpers/ClubContext";
import { Col, Row } from "react-bootstrap";
import { CardType, PlayerStatus } from "src/app/enums/Index";
import { ResourceType } from "src/app/enums/ResourceType";
import { Fixture, Player, Resource } from "src/app/interfaces/Index";
import { CardService, FixtureService, GoalService, PlayerService, ResourceService } from "src/app/services/Index";
import { BoardService } from "src/app/services/BoardService";
import { PenaltyService } from "src/app/services/PenaltyService";
import { ScoreboardCommandService } from "src/app/services/ScoreboardCommandService";
import { ScoreboardStateService } from "src/app/services/ScoreboardStateService";
import { SubstituteService } from "src/app/services/SubstituteService";
import { environment } from "src/environments/environment";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import "./AdminRemote.css";

// Cloned from the Pitchbase mobile app display page
const SCREENS = ["Latest", "Partners", "Halftime Draw", "Additional Time", "Attendance", "Team", "General", "Latest Scores", "POTM"];
const VIDEOS: { label: string, value: string }[] = [
	{ label: "Corner", value: "https://sufc.blob.core.windows.net/uploads/CORNER2526.mp4" },
	{ label: "Iron", value: "Iron" },
	{ label: "United", value: "United" },
	{ label: "Goal", value: "Goal" },
	{ label: "Penalty", value: "Penalty" },
	{ label: "Yellow", value: "Yellow" }
];
const TEXT_SIZES = ["9", "10", "13", "16", "18", "24", "32"];

type Props = {};
type State = {
	commands: any[];
	confirmHalf: string;
	fixture: Fixture;
	homePlayers: Player[];
	awayPlayers: Player[];
	libraryType: string;
	pickerMode: string;
	pickerTeam: number;
	playerOff: Player | null;
	resources: Resource[];
	showGraphicChooser: boolean;
	showLibrary: boolean;
	showPicker: boolean;
	showTextDialog: boolean;
	showTextSizeChooser: boolean;
	showTimerDialog: boolean;
	showVideoChooser: boolean;
	editFixture: any;
	showFixtureModal: boolean;
	textValue: string;
	timerSeconds: string;
	boardOnline: boolean | null;
	penalties: any[];
	showShootout: boolean;
	boards: any[];
	targetBoardIds: number[];
	onlineBoardIds: number[];
};

export class AdminRemote extends React.Component<Props, State> {
	toast: any;
	refreshTimer: any;
	hub: HubConnection | null = null;

	// Local timer model — the state document carries the clock as a base value +
	// an anchor, so the board can reconstruct the live time. The remote tracks it
	// (seeded from the current state) rather than reconstructing from commands.
	timerRunning = false;
	timerBaseSeconds = 0;
	timerAnchorMs: number | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {
			commands: [], confirmHalf: "", fixture: new Fixture(), homePlayers: [], awayPlayers: [],
			libraryType: "Images", pickerMode: "", pickerTeam: 1, playerOff: null, resources: [],
			showGraphicChooser: false, showLibrary: false, showPicker: false, showTextDialog: false,
			showTextSizeChooser: false, showTimerDialog: false, showVideoChooser: false, editFixture: {}, showFixtureModal: false, textValue: "", timerSeconds: "", boardOnline: null,
			penalties: [], showShootout: false, boards: [], targetBoardIds: [], onlineBoardIds: []
		};
	}

	componentDidMount() {
		this.getFixture();
		this.getCommands();
		this.getBoards();
		this.loadState();
		this.loadBoardStatus();
		this.connectHub();
		// SignalR pushes drive the live view; this poll is just a safety net now.
		this.refreshTimer = setInterval(() => { this.getCommands(); this.refreshFixture(); this.loadBoardStatus(); }, 5000);
	}

	componentWillUnmount() {
		clearInterval(this.refreshTimer);
		this.hub?.stop();
	}

	/**
	 * Live updates over SignalR: subscribe to this club's group and refresh the
	 * view the instant a command or state change is broadcast (by this remote,
	 * the mobile app, or the board), instead of waiting for the poll.
	 */
	async connectHub() {
		try {
			const clubId = ClubContext.club?.id ?? 1;
			const hub = new HubConnectionBuilder()
				.withUrl(`${environment.server.apiURL}/hubs/scoreboard`)
				.withAutomaticReconnect()
				.build();
			const refresh = () => { this.getCommands(); this.refreshFixture(); };
			hub.on("ReceiveState", refresh);
			hub.on("ReceiveCommand", refresh);
			hub.on("ReceiveShootout", (payload: any) => {
				if (payload?.fixtureId === this.state.fixture?.id) this.setState({ penalties: payload.penalties || [] });
			});
			hub.on("BoardPresence", (presence: any) => this.setState({
				boardOnline: !!presence?.online,
				onlineBoardIds: Array.isArray(presence?.boards) ? presence.boards : this.state.onlineBoardIds
			}));
			hub.onreconnected(() => hub.invoke("Subscribe", clubId).catch(() => { }));
			await hub.start();
			await hub.invoke("Subscribe", clubId);
			this.hub = hub;
		} catch { /* falls back to the safety-net poll */ }
	}

	command(key: string) {
		return this.state.commands.find((x) => x.commandKey === key);
	}

	async getCommands() {
		try {
			const res = await safeJson(ScoreboardCommandService.getCommands(1));
			if (Array.isArray(res)) this.setState({ commands: res });
		} catch { /* offline */ }
	}

	async getFixture() {
		const res = await safeJson(FixtureService.getLatest());
		if (res && !res.error) {
			this.setState({ fixture: res });
			this.getPlayers(res.teamId);
			this.getPenalties(res.id);
		}
	}

	// ---------- Penalty shootout ----------

	async getPenalties(fixtureId: number) {
		if (!fixtureId) return;
		try {
			const res = await safeJson(PenaltyService.getByFixture(fixtureId));
			if (Array.isArray(res)) this.setState({ penalties: res });
		} catch { /* pre-shootout API */ }
	}

	/** Kicks a team has taken, in order. */
	kicksFor(teamId: number) {
		return this.state.penalties.filter((p) => p.teamId === teamId).sort((a, b) => a.shotOrder - b.shotOrder);
	}

	shootoutScore(teamId: number) {
		return this.kicksFor(teamId).filter((p) => p.scored).length;
	}

	async recordPenalty(teamId: number, scored: boolean) {
		const fixtureId = this.state.fixture.id;
		if (!fixtureId || !teamId) return;
		const shotOrder = this.kicksFor(teamId).length + 1;
		const res = await PenaltyService.record({ fixtureId, teamId, shotOrder, scored });
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Something went wrong!" });
			return;
		}
		this.getPenalties(fixtureId);
	}

	async undoPenalty() {
		const fixtureId = this.state.fixture.id;
		if (!fixtureId) return;
		await PenaltyService.undoLast(fixtureId);
		this.getPenalties(fixtureId);
	}

	/**
	 * Toggle the board's Penalties screen (mirrors the mobile app). Activating
	 * first asks how many shots each team will take and saves it on the fixture —
	 * the board draws that many kick slots.
	 */
	async activateShootout() {
		if ((this.command("Screen")?.commandValue || "Scoreboard") === "Penalties") {
			this.updateCommand("Screen", "Scoreboard");
			return;
		}
		const fixture = this.state.fixture;
		const current = parseInt(String(fixture.penaltyShots)) || 5;
		const answer = window.prompt("How many shots will be taken (per team)?", String(current));
		if (answer == null) return;
		const shots = Math.max(1, parseInt(answer) || current);
		const updated = { ...fixture, penaltyShots: shots };
		const res = await FixtureService.updateSingleById(fixture.id, updated);
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Something went wrong!" });
			return;
		}
		this.setState({ fixture: updated });
		this.updateCommand("Screen", "Penalties");
	}

	async refreshFixture() {
		try {
			const res = await safeJson(FixtureService.getLatest());
			if (res && !res.error) {
				if (res.teamId !== this.state.fixture.teamId) this.getPlayers(res.teamId);
				this.setState({ fixture: res });
			}
		} catch { /* offline tick */ }
	}

	async getPlayers(awayTeamId: number) {
		const home = await safeJson(PlayerService.getHomeTeam());
		if (Array.isArray(home)) this.setState({ homePlayers: home });
		if (awayTeamId) {
			const away = await safeJson(PlayerService.getByTeam(awayTeamId));
			if (Array.isArray(away)) this.setState({ awayPlayers: away });
		}
	}

	// ---------- Local timer model ----------

	/** Live seconds from the base + anchor. */
	currentTimerSeconds() {
		return this.timerRunning && this.timerAnchorMs != null
			? this.timerBaseSeconds + Math.floor((Date.now() - this.timerAnchorMs) / 1000)
			: this.timerBaseSeconds;
	}

	/** Update the local timer model when a timer command is issued. */
	applyTimerLocally(key: string, value: string) {
		if (key === "TimerStatus") {
			if (value === "Running") {
				if (!this.timerRunning) { this.timerBaseSeconds = this.currentTimerSeconds(); this.timerAnchorMs = Date.now(); this.timerRunning = true; }
			} else {
				this.timerBaseSeconds = this.currentTimerSeconds(); this.timerRunning = false; this.timerAnchorMs = null;
			}
		} else if (key === "SetTimer") {
			this.timerBaseSeconds = Number(value) || 0;
			this.timerAnchorMs = this.timerRunning ? Date.now() : null;
		} else if (key === "TimerMode") {
			// Second half starts at the fixture's half length, not a fixed 45.
			this.timerBaseSeconds = Number(value) === 2 ? this.halfLengthSeconds() : 0;
			this.timerAnchorMs = this.timerRunning ? Date.now() : null;
		}
	}

	// ---------- Board targeting ----------

	async getBoards() {
		try {
			const res = await safeJson(BoardService.getMine());
			if (Array.isArray(res)) this.setState({ boards: res });
		} catch { /* pre-registry API */ }
	}

	/** Toggle a board in/out of the target set. Empty = all boards. */
	toggleTargetBoard(id: number) {
		const targets = this.state.targetBoardIds.includes(id)
			? this.state.targetBoardIds.filter((x) => x !== id)
			: [...this.state.targetBoardIds, id];
		this.setState({ targetBoardIds: targets });
	}

	renderBoardTargets() {
		const { boards, targetBoardIds, onlineBoardIds } = this.state;
		// Nothing to choose between until a club registers a second board.
		if (boards.length < 2) return null;
		return (
			<div className="remote-targets">
				<span className="remote-targets-label">Controlling</span>
				<button className={`remote-target${targetBoardIds.length === 0 ? " active" : ""}`} onClick={() => this.setState({ targetBoardIds: [] })} type="button">All boards</button>
				{boards.map((board) => (
					<button className={`remote-target${targetBoardIds.includes(board.id) ? " active" : ""}`} key={`tb-${board.id}`} onClick={() => this.toggleTargetBoard(board.id)} type="button">
						<span className={`remote-target-dot${onlineBoardIds.includes(board.id) ? " live" : ""}`} />
						{board.name}
					</button>
				))}
			</div>
		);
	}

	/** Seconds in one half, from the fixture's match format (default 45 min). */
	halfLengthSeconds() {
		return (parseInt(String(this.state.fixture?.minutesPerHalf)) || 45) * 60;
	}

	/** Whether the physical board has a live hub connection right now. */
	async loadBoardStatus() {
		try {
			const res = await safeJson(ScoreboardStateService.getBoardStatus());
			if (res && typeof res.online === "boolean") this.setState({ boardOnline: res.online });
		} catch { /* API without presence support — indicator stays hidden */ }
	}

	/** Seed the local timer model from the board's current state on load. */
	async loadState() {
		try {
			const res = await safeJson(ScoreboardStateService.getState());
			if (res) {
				this.timerRunning = !!res.timerRunning;
				this.timerBaseSeconds = Number(res.timerSeconds) || 0;
				this.timerAnchorMs = res.timerAnchor ? Date.parse(res.timerAnchor) : null;
			}
		} catch { /* defaults stand */ }
	}

	/** Build the full scoreboard state, applying a pending change (override). */
	buildState(overrideKey?: string, overrideValue?: string) {
		const val = (key: string) => overrideKey === key ? overrideValue : this.command(key)?.commandValue;
		return {
			display: val("Display") !== "False",
			timerMode: Number(val("TimerMode")) === 2 ? 2 : 1,
			timerRunning: this.timerRunning,
			timerSeconds: this.timerBaseSeconds,
			timerAnchor: this.timerAnchorMs != null ? new Date(this.timerAnchorMs).toISOString() : null,
			screen: val("Screen") || "Scoreboard",
			video: val("Video") || "Scoreboard",
			text: val("Text") || "Scoreboard",
			textSize: Number(val("TextSize")) || 45,
		};
	}

	/**
	 * Send a control change. The state document is the primary channel (persisted +
	 * broadcast as ReceiveState); the ScoreboardCommand row is written as a backup
	 * when one exists, so the board still gets it via ReceiveCommand and the mobile
	 * app keeps working.
	 */
	async updateCommand(key: string, value: string) {
		this.applyTimerLocally(key, value);

		let ok = false;
		try {
			// Empty selection = every board in the club.
			const targets = this.state.targetBoardIds.join(",");
			const res = await ScoreboardStateService.saveState(this.buildState(key, value), targets || undefined);
			ok = res.ok;
		} catch { /* handled below */ }

		// Backup path: update the command row if the club has one.
		const command = this.command(key);
		if (command) {
			ScoreboardCommandService.updateSingleById(command.id, { ...command, commandValue: value, commandComplete: false }).catch(() => { });
		}

		if (!ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Something went wrong!" });
			return;
		}
		this.toast.show({ closable: false, detail: `${key} → ${value.length > 40 ? value.slice(0, 40) + "…" : value}`, life: 2500, severity: "success", summary: "Sent to scoreboard" });
		this.getCommands();
	}

	// ---------- Match control (mobile control page + team modal) ----------

	openPicker(mode: string, team: number) {
		this.setState({ pickerMode: mode, pickerTeam: team, playerOff: null, showPicker: true });
	}

	pickerPlayers() {
		const squad = this.state.pickerTeam === ClubContext.homeTeamId ? this.state.homePlayers : this.state.awayPlayers;
		const status = this.state.pickerMode === "SUB_ON" ? PlayerStatus.Substitute : PlayerStatus.Starting;
		return squad.filter((x) => x.playerStatus === status).sort((a, b) => a.playerNumber - b.playerNumber);
	}

	async playerTapped(player: Player) {
		const { pickerMode, pickerTeam } = this.state;
		if (pickerMode === "GOAL") {
			await GoalService.createSingle({ fixtureId: this.state.fixture.id, playerId: player.id } as any);
			const fixture = { ...this.state.fixture };
			if (pickerTeam === ClubContext.homeTeamId) fixture.homeScore = (fixture.homeScore || 0) + 1;
			else fixture.awayScore = (fixture.awayScore || 0) + 1;
			await FixtureService.updateSingleById(fixture.id, fixture as any);
			this.setState({ fixture, showPicker: false });
			this.toast.show({ closable: false, detail: `Goal! ${player.playerName}`, life: 3000, severity: "success", summary: pickerTeam === ClubContext.homeTeamId ? "GOAL!!!" : "Away goal" });
		} else if (pickerMode === "YELLOW" || pickerMode === "RED") {
			await CardService.createSingle({ fixtureId: this.state.fixture.id, playerId: player.id, cardType: pickerMode === "YELLOW" ? CardType.Yellow : CardType.Red } as any);
			this.setState({ showPicker: false });
			this.toast.show({ closable: false, detail: `${pickerMode === "YELLOW" ? "Yellow" : "Red"} card - ${player.playerName}`, life: 3000, severity: "warn", summary: "Card recorded" });
		} else if (pickerMode === "SUB_OFF") {
			this.setState({ playerOff: player, pickerMode: "SUB_ON" });
		} else if (pickerMode === "SUB_ON") {
			const playerOff = this.state.playerOff!;
			await SubstituteService.createSingle({ fixtureId: this.state.fixture.id, playerOffId: playerOff.id, playerOnId: player.id } as any);
			await PlayerService.updateSingleById(playerOff.id, { ...playerOff, playerStatus: PlayerStatus.Substitute } as any);
			await PlayerService.updateSingleById(player.id, { ...player, playerStatus: PlayerStatus.Starting } as any);
			this.setState({ showPicker: false });
			this.toast.show({ closable: false, detail: `${playerOff.playerName} off, ${player.playerName} on`, life: 3000, severity: "success", summary: "Substitution" });
			this.getPlayers(this.state.fixture.teamId);
		}
	}

	// ---------- Fixture details (mobile fixtureDetails modal) ----------

	editField(name: string, value: any) {
		this.setState({ editFixture: { ...this.state.editFixture, [name]: value } });
	}

	stepAdditional(field: string, delta: number) {
		const current = parseInt(this.state.editFixture[field]) || 0;
		this.editField(field, Math.max(0, current + delta));
	}

	openFixtureModal() {
		this.setState({ editFixture: { ...this.state.fixture }, showFixtureModal: true });
	}

	async saveFixtureModal() {
		const res = await FixtureService.updateSingleById(this.state.editFixture.id, this.state.editFixture);
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Something went wrong!" });
			return;
		}
		this.setState({ fixture: this.state.editFixture, showFixtureModal: false });
		this.toast.show({ closable: false, detail: "Fixture updated.", life: 2500, severity: "success", summary: "Saved" });
	}

	renderFixtureField(label: string, field: string, keyfilter: any = "int") {
		return (
			<div className="field" style={{ marginBottom: 10 }}>
				<label htmlFor={field}>{label}</label>
				<InputText id={field} keyfilter={keyfilter} onChange={(e) => this.editField(field, e.target.value)} style={{ width: "100%" }} value={this.state.editFixture[field] ?? ""} />
			</div>
		);
	}

	// ---------- Display control (mobile display page) ----------

	toggleDisplay() {
		this.updateCommand("Display", this.command("Display")?.commandValue === "True" ? "False" : "True");
	}

	toggleTimer() {
		this.updateCommand("TimerStatus", this.command("TimerStatus")?.commandValue === "Stop" ? "Running" : "Stop");
	}

	/** Tell the board to re-pull everything: fixture, home & away line-ups, sponsors, theme. */
	sendRefresh() {
		this.updateCommand("Refresh", "Now");
	}

	/** Parse an "MM:SS" (or plain seconds) entry into whole seconds. */
	parseTimerSeconds(value: string): number | null {
		const v = (value || "").trim();
		if (!v) return null;
		if (v.includes(":")) {
			const [m, s] = v.split(":");
			return (parseInt(m, 10) || 0) * 60 + (parseInt(s, 10) || 0);
		}
		const n = parseInt(v, 10);
		return Number.isNaN(n) ? null : n;
	}

	graphicButton() {
		if (this.command("Screen")?.commandValue !== "Scoreboard") this.updateCommand("Screen", "Scoreboard");
		else this.setState({ showGraphicChooser: true });
	}

	videoButton() {
		if (this.command("Video")?.commandValue !== "Scoreboard") this.updateCommand("Video", "Scoreboard");
		else this.setState({ showVideoChooser: true });
	}

	textButton() {
		if (this.command("Text")?.commandValue !== "Scoreboard") this.updateCommand("Text", "Scoreboard");
		else this.setState({ showTextDialog: true, textValue: "" });
	}

	async openLibrary(type: string) {
		this.setState({ libraryType: type, showGraphicChooser: false, showVideoChooser: false });
		const res = await safeJson(ResourceService.getAll());
		const wanted = type === "Videos" ? ResourceType.Video : ResourceType.Image;
		this.setState({ resources: (Array.isArray(res) ? res : []).filter((x) => Number(x.resourceType) === wanted), showLibrary: true });
	}

	pickResource(resource: Resource) {
		this.setState({ showLibrary: false });
		this.updateCommand(this.state.libraryType === "Videos" ? "Video" : "Screen", resource.resourceUrl);
	}

	screenRoute(value: string) {
		const routes: { [key: string]: string } = {
			"Latest": "latest", "Partners": "partners", "Halftime Draw": "ht-draw", "Additional Time": "additional",
			"Attendance": "attendance", "Team": "team", "General": "general", "Latest Scores": "latest-scores", "POTM": "potm"
		};
		return routes[value] ? `/${environment.build.scoreboardRoute}/${routes[value]}` : null;
	}

	renderPreview(screenValue: string, videoValue: string, textValue: string) {
		const showing = screenValue !== "Scoreboard" || videoValue !== "Scoreboard" || textValue !== "Scoreboard";
		if (!showing) return null;
		const isUrl = (value: string) => /^https?:\/\//.test(value);
		const textSize = Number(this.command("TextSize")?.commandValue) || 16;
		return (
			<div className="card" style={{ marginTop: "1.5rem", padding: "1.5rem" }}>
				<h5 style={{ marginBottom: "1rem" }}>Now Showing <small style={{ color: "var(--pb-text-muted)", fontWeight: 700 }}>(board is 576 \u00d7 432)</small></h5>
				<div className="remote-previews">
					{screenValue !== "Scoreboard" &&
						<div className="remote-preview">
							<span className="remote-preview-label">Graphic - {isUrl(screenValue) ? "Library image" : screenValue}</span>
							<div className="remote-preview-frame">
								{isUrl(screenValue)
									? <img alt="Graphic preview" src={screenValue} />
									: this.screenRoute(screenValue)
										? <iframe src={this.screenRoute(screenValue)!} title="Scoreboard preview" />
										: <div className="remote-preview-unknown">{screenValue}</div>
								}
							</div>
						</div>
					}
					{videoValue !== "Scoreboard" &&
						<div className="remote-preview">
							<span className="remote-preview-label">Video - {isUrl(videoValue) ? "Playing" : videoValue}</span>
							<div className="remote-preview-frame">
								{isUrl(videoValue)
									? <video autoPlay loop muted src={videoValue} />
									: <div className="remote-preview-unknown">"{videoValue}" video playing on the board</div>
								}
							</div>
						</div>
					}
					{textValue !== "Scoreboard" &&
						<div className="remote-preview">
							<span className="remote-preview-label">Text (size {textSize})</span>
							<div className="remote-preview-frame">
								<div className="remote-preview-text" style={{ fontSize: Math.max(14, textSize * 2) }}>{textValue}</div>
							</div>
						</div>
					}
				</div>
			</div>
		);
	}

	/** One team's shootout row: score, kick markers, and score/miss buttons. */
	renderShootoutTeam(teamId: number, name: string) {
		const kicks = this.kicksFor(teamId);
		const planned = parseInt(String(this.state.fixture.penaltyShots)) || 5;
		const slots = Math.max(planned, kicks.length);
		return (
			<div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "14px 0" }}>
				<div style={{ alignItems: "center", display: "flex", gap: 12, marginBottom: 10 }}>
					<strong style={{ minWidth: 90 }}>{name}</strong>
					<span style={{ fontSize: "1.4rem", fontWeight: 800 }}>{this.shootoutScore(teamId)}</span>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
						{Array.from({ length: slots }, (_, i) => {
							const kick = kicks[i];
							const bg = !kick ? "transparent" : kick.scored ? "#12B981" : "#B22222";
							return (
								<span key={i} style={{ alignItems: "center", background: bg, border: kick ? "none" : "2px solid rgba(255,255,255,0.3)", borderRadius: "50%", display: "flex", fontSize: 14, fontWeight: 700, height: 26, justifyContent: "center", width: 26 }}>
									{kick ? (kick.scored ? "✓" : "✗") : ""}
								</span>
							);
						})}
					</div>
				</div>
				<div style={{ display: "flex", gap: 10 }}>
					<Button className="p-button-success" icon="pi pi-check" label="Scored" onClick={() => this.recordPenalty(teamId, true)}></Button>
					<Button className="p-button-danger" icon="pi pi-times" label="Missed" onClick={() => this.recordPenalty(teamId, false)}></Button>
				</div>
			</div>
		);
	}

	renderTile(label: string, icon: any, className: string, onClick: () => void, sub?: string) {
		return (
			<div className={`remote-tile ${className}`} onClick={onClick}>
				<FontAwesomeIcon icon={icon} />
				<span>{label}</span>
				{sub && <small>{sub}</small>}
			</div>
		);
	}

	render() {
		const { fixture } = this.state;
		const displayOn = this.command("Display")?.commandValue === "True";
		const timerRunning = this.command("TimerStatus")?.commandValue === "Running";
		const screenValue = this.command("Screen")?.commandValue || "Scoreboard";
		const videoValue = this.command("Video")?.commandValue || "Scoreboard";
		const textValue = this.command("Text")?.commandValue || "Scoreboard";
		const awayName = fixture.team?.teamNameShort || fixture.team?.teamName || "AWAY";

		return (
			<>
				<div className="card" style={{ padding: "1.5rem" }}>
					{this.renderBoardTargets()}
					<div className="remote-columns">
						<div className="remote-column">
							<div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
								<h5 style={{ margin: 0 }}>Match Control</h5>
								<Button className="p-button-outlined" icon="pi pi-pencil" label="Fixture" onClick={() => this.openFixtureModal()}></Button>
							</div>
							<div className="remote-hero">
								<div className="remote-hero-team">
									<img alt="Scunthorpe United" src="/assets/Badge.png" />
									<span>IRON</span>
								</div>
								<div className="remote-hero-centre">
									<strong>{fixture.homeScore ?? 0} - {fixture.awayScore ?? 0}</strong>
									<span>{fixture.fixtureDateTime ? new Date(fixture.fixtureDateTime).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
								</div>
								<div className="remote-hero-team">
									{fixture.team?.badgeUrl
										? <img alt={awayName} src={fixture.team.badgeUrl} />
										: <div className="remote-hero-noBadge">{(awayName || "?").substring(0, 3)}</div>
									}
									<span>{awayName}</span>
								</div>
							</div>
							<div className="remote-cols-header"><span>Home</span><span>Away</span></div>
							<div className="remote-grid">
								{this.renderTile("Goal", faFutbol, "goal", () => this.openPicker("GOAL", ClubContext.homeTeamId))}
								{this.renderTile("Goal", faFutbol, "goal", () => this.openPicker("GOAL", fixture.teamId))}
								{this.renderTile("Sub", faRightLeft, "sub", () => this.openPicker("SUB_OFF", ClubContext.homeTeamId))}
								{this.renderTile("Sub", faRightLeft, "sub", () => this.openPicker("SUB_OFF", fixture.teamId))}
								{this.renderTile("Card", faTriangleExclamation, "yellow", () => this.openPicker("YELLOW", ClubContext.homeTeamId))}
								{this.renderTile("Card", faTriangleExclamation, "yellow", () => this.openPicker("YELLOW", fixture.teamId))}
								{this.renderTile("Card", faBan, "red", () => this.openPicker("RED", ClubContext.homeTeamId))}
								{this.renderTile("Card", faBan, "red", () => this.openPicker("RED", fixture.teamId))}
								{this.renderTile("Shootout", faBullseye, "neutral remote-tile-wide", () => this.setState({ showShootout: true }), `${this.shootoutScore(ClubContext.homeTeamId)} - ${this.shootoutScore(fixture.teamId)}`)}
							</div>
						</div>
						<div className="remote-divider" />
						<div className="remote-column">
							<div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: "1rem", minHeight: "2.55rem" }}>
								<h5 style={{ margin: 0 }}>Display Control</h5>
								{this.state.boardOnline != null &&
									<span className={`remote-board-status ${this.state.boardOnline ? "online" : "offline"}`}>
										<span className="remote-board-dot" />
										{this.state.boardOnline ? "Board connected" : "Board offline"}
									</span>
								}
							</div>
							<div className="remote-grid">
								{this.renderTile(displayOn ? "Display Off" : "Display On", faPowerOff, displayOn ? "display-on" : "display-off", () => this.toggleDisplay())}
								{this.renderTile(timerRunning ? "Stop Timer" : "Start Timer", faStopwatch, timerRunning ? "display-on" : "neutral", () => this.toggleTimer())}
								{this.renderTile("1st Half", faClockRotateLeft, "neutral", () => this.setState({ confirmHalf: "1" }))}
								{this.renderTile("2nd Half", faClockRotateLeft, "neutral", () => this.setState({ confirmHalf: "2" }))}
								{this.renderTile("Set Timer", faStopwatch, "neutral", () => this.setState({ showTimerDialog: true, timerSeconds: "" }))}
								{this.renderTile("Refresh", faArrowsRotate, "neutral", () => this.sendRefresh(), "fixture · squads")}
								{this.renderTile(screenValue === "Scoreboard" ? "Graphics" : "End Graphic", faImage, screenValue === "Scoreboard" ? "neutral" : "active", () => this.graphicButton(), screenValue === "Scoreboard" ? undefined : "showing")}
								{this.renderTile(videoValue === "Scoreboard" ? "Video" : "End Video", faVideo, videoValue === "Scoreboard" ? "neutral" : "active", () => this.videoButton(), videoValue === "Scoreboard" ? undefined : "playing")}
								{this.renderTile(textValue === "Scoreboard" ? "Text" : "End Text", faFileLines, textValue === "Scoreboard" ? "neutral" : "active", () => this.textButton(), textValue === "Scoreboard" ? undefined : "showing")}
								{this.renderTile("Text Size", faTextHeight, "neutral", () => this.setState({ showTextSizeChooser: true }))}
							</div>
						</div>
					</div>
				</div>

				{this.renderPreview(screenValue, videoValue, textValue)}

				{/* Fixture details (mobile fixtureDetails modal) */}
				<Dialog className="p-fluid" footer={<>
					<Button className="p-button-text" icon="pi pi-times" label="Cancel" onClick={() => this.setState({ showFixtureModal: false })}></Button>
					<Button className="p-button-text" icon="pi pi-check" label="Save" onClick={() => this.saveFixtureModal()}></Button>
				</>} header={`Scunthorpe United vs ${this.state.fixture.team?.teamName || ""}`} modal onHide={() => this.setState({ showFixtureModal: false })} style={{ width: 640 }} visible={this.state.showFixtureModal}>
					<TabView>
						<TabPanel header="Half Time Draw" leftIcon="pi pi-ticket">
							<Row>
								<Col>{this.renderFixtureField("1st - Winning Number", "htDrawFirst")}</Col>
								<Col>{this.renderFixtureField("1st - Prize (\u00a3)", "htDrawFirstPrize")}</Col>
							</Row>
							<Row>
								<Col>{this.renderFixtureField("2nd - Winning Number", "htDrawSecond")}</Col>
								<Col>{this.renderFixtureField("2nd - Prize (\u00a3)", "htDrawSecondPrize")}</Col>
							</Row>
							<Row>
								<Col>{this.renderFixtureField("3rd - Winning Number", "htDrawThird")}</Col>
								<Col>{this.renderFixtureField("3rd - Prize (\u00a3)", "htDrawThirdPrize")}</Col>
							</Row>
						</TabPanel>
						<TabPanel header="Attendance" leftIcon="pi pi-users">
							<Row>
								<Col>{this.renderFixtureField("Home Fans", "matchAttendance")}</Col>
								<Col>{this.renderFixtureField("Away Fans", "matchAwayAttendance")}</Col>
							</Row>
							<div className="remote-attendance-total">
								<span>Total Attendance</span>
								<strong>{(parseInt(this.state.editFixture.matchAttendance) || 0) + (parseInt(this.state.editFixture.matchAwayAttendance) || 0)}</strong>
							</div>
						</TabPanel>
						<TabPanel header="Additional Time" leftIcon="pi pi-clock">
							<Row>
								<Col>
									<label>1st Half Additional</label>
									<div className="remote-stepper">
										<Button icon="pi pi-minus" className="p-button-outlined" onClick={() => this.stepAdditional("firstHalfAdditional", -1)}></Button>
										<span>{parseInt(this.state.editFixture.firstHalfAdditional) || 0}</span>
										<Button icon="pi pi-plus" className="p-button-outlined" onClick={() => this.stepAdditional("firstHalfAdditional", 1)}></Button>
									</div>
								</Col>
								<Col>
									<label>2nd Half Additional</label>
									<div className="remote-stepper">
										<Button icon="pi pi-minus" className="p-button-outlined" onClick={() => this.stepAdditional("secondHalfAdditional", -1)}></Button>
										<span>{parseInt(this.state.editFixture.secondHalfAdditional) || 0}</span>
										<Button icon="pi pi-plus" className="p-button-outlined" onClick={() => this.stepAdditional("secondHalfAdditional", 1)}></Button>
									</div>
								</Col>
							</Row>
						</TabPanel>
						<TabPanel header="Match Format" leftIcon="pi pi-stopwatch">
							<Row>
								<Col>
									<label>Minutes per Half</label>
									<div className="remote-stepper">
										<Button icon="pi pi-minus" className="p-button-outlined" onClick={() => this.stepAdditional("minutesPerHalf", -1)}></Button>
										<span>{parseInt(this.state.editFixture.minutesPerHalf) || 45}</span>
										<Button icon="pi pi-plus" className="p-button-outlined" onClick={() => this.stepAdditional("minutesPerHalf", 1)}></Button>
									</div>
									<small style={{ color: "#9DA3AF" }}>The board shows ADDITIONAL TIME once the clock passes this.</small>
								</Col>
								<Col>
									<label>Penalty Shots (each team)</label>
									<div className="remote-stepper">
										<Button icon="pi pi-minus" className="p-button-outlined" onClick={() => this.stepAdditional("penaltyShots", -1)}></Button>
										<span>{parseInt(this.state.editFixture.penaltyShots) || 5}</span>
										<Button icon="pi pi-plus" className="p-button-outlined" onClick={() => this.stepAdditional("penaltyShots", 1)}></Button>
									</div>
									<small style={{ color: "#9DA3AF" }}>Kicks shown on the shootout board before sudden death.</small>
								</Col>
							</Row>
						</TabPanel>
					</TabView>
				</Dialog>

				{/* Penalty shootout */}
				<Dialog footer={<>
					<Button className="p-button-text" icon="pi pi-undo" label="Undo last kick" onClick={() => this.undoPenalty()}></Button>
					<Button className="p-button-text" icon="pi pi-check" label="Done" onClick={() => this.setState({ showShootout: false })}></Button>
				</>} header="Penalty Shootout" modal onHide={() => this.setState({ showShootout: false })} style={{ width: 640 }} visible={this.state.showShootout}>
					<Button className={screenValue === "Penalties" ? "p-button-danger" : ""} icon="pi pi-desktop"
						label={screenValue === "Penalties" ? "Deactivate" : "Activate"} style={{ marginBottom: 6, width: "100%" }}
						onClick={() => this.activateShootout()}></Button>
					{this.renderShootoutTeam(ClubContext.homeTeamId, "IRON")}
					{this.renderShootoutTeam(this.state.fixture.teamId, awayName)}
					<div style={{ color: "#9DA3AF", fontSize: 12, marginTop: 12 }}>
						Kicks appear on the board instantly. Activate shows the shootout screen on the board; Deactivate returns it to the scoreboard.
					</div>
				</Dialog>

				{/* Player picker */}
				<Dialog header={this.state.pickerMode === "SUB_ON" ? `On for ${this.state.playerOff?.playerName || ""}` : this.state.pickerMode === "SUB_OFF" ? "Player coming off" : `Pick a player`} modal onHide={() => this.setState({ showPicker: false })} style={{ width: 700 }} visible={this.state.showPicker}>
					<div className="remote-picker-grid">
						{this.pickerPlayers().map((player) => (
							<div className="remote-picker-player" key={`rp-${player.id}`} onClick={() => this.playerTapped(player)}>
								{player.mugshotUrl
									? <img alt={player.playerName} src={player.mugshotUrl} />
									: <div className="remote-picker-shirt"><FontAwesomeIcon icon={faShirt} /></div>
								}
								<div>{player.playerNumber}. {player.playerName}</div>
							</div>
						))}
						{this.pickerPlayers().length === 0 && <div style={{ color: "var(--pb-text-muted)" }}>No players available.</div>}
					</div>
				</Dialog>

				{/* Graphic chooser */}
				<Dialog header="Select graphic to display" modal onHide={() => this.setState({ showGraphicChooser: false })} style={{ width: 480 }} visible={this.state.showGraphicChooser}>
					<div className="remote-choice-list">
						{SCREENS.map((screen) => <Button className="p-button-outlined" key={screen} label={screen} onClick={() => { this.setState({ showGraphicChooser: false }); this.updateCommand("Screen", screen); }}></Button>)}
						<Button icon="pi pi-images" label="Library" style={{ backgroundColor: "#11B981", borderColor: "#11B981" }} onClick={() => this.openLibrary("Images")}></Button>
					</div>
				</Dialog>

				{/* Video chooser */}
				<Dialog header="Select a video to display" modal onHide={() => this.setState({ showVideoChooser: false })} style={{ width: 480 }} visible={this.state.showVideoChooser}>
					<div className="remote-choice-list">
						{VIDEOS.map((video) => <Button className="p-button-outlined" key={video.label} label={video.label} onClick={() => { this.setState({ showVideoChooser: false }); this.updateCommand("Video", video.value); }}></Button>)}
						<Button icon="pi pi-video" label="Library" style={{ backgroundColor: "#11B981", borderColor: "#11B981" }} onClick={() => this.openLibrary("Videos")}></Button>
					</div>
				</Dialog>

				{/* Resource library */}
				<Dialog header={`Library — ${this.state.libraryType}`} modal onHide={() => this.setState({ showLibrary: false })} style={{ width: 760 }} visible={this.state.showLibrary}>
					<div className="remote-library-grid">
						{this.state.resources.map((resource) => (
							<div className="remote-library-item" key={`lib-${resource.id}`} onClick={() => this.pickResource(resource)}>
								{Number(resource.resourceType) === ResourceType.Video
									? <video muted preload="metadata" src={`${resource.resourceUrl}#t=3`} />
									: <img alt={resource.name} loading="lazy" src={resource.resourceUrl} />
								}
								<span>{resource.name}</span>
							</div>
						))}
						{this.state.resources.length === 0 && <div style={{ color: "var(--pb-text-muted)" }}>Nothing in the library yet.</div>}
					</div>
				</Dialog>

				{/* Half confirm */}
				<Dialog footer={<>
					<Button className="p-button-text" icon="pi pi-times" label="Cancel" onClick={() => this.setState({ confirmHalf: "" })}></Button>
					<Button className="p-button-text" icon="pi pi-check" label="Yes" onClick={() => { this.updateCommand("TimerMode", this.state.confirmHalf); this.setState({ confirmHalf: "" }); }}></Button>
				</>} header="Confirm" modal onHide={() => this.setState({ confirmHalf: "" })} style={{ width: 420 }} visible={this.state.confirmHalf !== ""}>
					<span>Start {this.state.confirmHalf === "1" ? "1st" : "2nd"} half?</span>
				</Dialog>

				{/* Set timer */}
				<Dialog footer={<>
					<Button className="p-button-text" icon="pi pi-times" label="Cancel" onClick={() => this.setState({ showTimerDialog: false })}></Button>
					<Button className="p-button-text" icon="pi pi-check" label="Set" onClick={() => { const secs = this.parseTimerSeconds(this.state.timerSeconds); if (secs != null) this.updateCommand("SetTimer", String(secs)); this.setState({ showTimerDialog: false }); }}></Button>
				</>} header="Set timer" modal onHide={() => this.setState({ showTimerDialog: false })} style={{ width: 460 }} visible={this.state.showTimerDialog}>
					<p style={{ color: "var(--pb-text-muted)", marginTop: 0 }}>Enter the time as MM:SS (e.g. 45:00). Make sure the correct half is set first.</p>
					<InputText onChange={(e) => this.setState({ timerSeconds: e.target.value })} placeholder="MM:SS" style={{ width: "100%" }} value={this.state.timerSeconds} />
				</Dialog>

				{/* Scoreboard text */}
				<Dialog footer={<>
					<Button className="p-button-text" icon="pi pi-times" label="Cancel" onClick={() => this.setState({ showTextDialog: false })}></Button>
					<Button className="p-button-text" icon="pi pi-check" label="Send" onClick={() => { if (this.state.textValue) this.updateCommand("Text", this.state.textValue); this.setState({ showTextDialog: false }); }}></Button>
				</>} header="Scoreboard text" modal onHide={() => this.setState({ showTextDialog: false })} style={{ width: 460 }} visible={this.state.showTextDialog}>
					<InputText onChange={(e) => this.setState({ textValue: e.target.value })} placeholder="Text to display on the scoreboard" style={{ width: "100%" }} value={this.state.textValue} />
				</Dialog>

				{/* Text size */}
				<Dialog header="Select text size" modal onHide={() => this.setState({ showTextSizeChooser: false })} style={{ width: 420 }} visible={this.state.showTextSizeChooser}>
					<div className="remote-choice-list">
						{TEXT_SIZES.map((size) => <Button className="p-button-outlined" key={size} label={size} onClick={() => { this.setState({ showTextSizeChooser: false }); this.updateCommand("TextSize", size); }}></Button>)}
					</div>
				</Dialog>

				<Toast ref={(el) => this.toast = el}></Toast>
			</>
		);
	}
}
